import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { signIn, signUp } from '@/lib/auth-client'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { api } from '@/services/api'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [isSignUp, setIsSignUp] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setInfo(null)

    try {
      if (isSignUp) {
        // Validate registration first
        try {
          const validation = await api.validateRegistration(email)
          if (!validation.data.allowed) {
            setError(validation.data.error || 'Registration is not allowed for this email domain')
            setLoading(false)
            return
          }
          
          // Show info if first user
          if (validation.data.isFirstUser) {
            setInfo('You will be the first user and will be assigned the Admin role.')
          }
        } catch (validationError) {
          // If validation endpoint is not available, proceed with signup anyway
          console.warn('Registration validation failed, proceeding:', validationError)
        }

        const { data, error } = await signUp.email({
          email,
          password,
          name,
        })
        
        if (error) {
          // Check for domain-related errors in the message
          const errorMessage = error.message || 'Sign up failed'
          if (errorMessage.includes('domain') || errorMessage.includes('restricted') || errorMessage.includes('approved')) {
            setError('Registration is restricted to approved domains. Please contact your administrator.')
          } else {
            setError(errorMessage)
          }
          setLoading(false)
          return
        }
        
        if (data) {
          navigate('/')
        }
      } else {
        const { data, error } = await signIn.email({
          email,
          password,
        })
        
        if (error) {
          setError(error.message || 'Sign in failed')
          setLoading(false)
          return
        }
        
        if (data) {
          navigate('/')
        }
      }
    } catch (err) {
      console.error('Auth error:', err)
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed'
      // Check for domain-related errors
      if (errorMessage.includes('domain') || errorMessage.includes('restricted') || errorMessage.includes('approved')) {
        setError('Registration is restricted to approved domains. Please contact your administrator.')
      } else {
        setError(errorMessage)
      }
      setLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-slate-100 px-4 py-12 sm:px-6 lg:px-8">
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            'radial-gradient(800px 300px at 50% 0%, rgba(37, 99, 235, 0.18), transparent 60%), radial-gradient(700px 300px at 20% 10%, rgba(6, 182, 212, 0.16), transparent 55%)',
        }}
      />
      <div className="w-full max-w-md space-y-8">
        <div>
          <div className="flex items-center justify-center gap-3">
            <svg className="h-10 w-10" viewBox="0 0 64 64" aria-hidden="true">
              <circle cx="21" cy="32" r="18" fill="none" stroke="#2563EB" strokeWidth="2.5" />
              <circle cx="43" cy="32" r="18" fill="none" stroke="#06B6D4" strokeWidth="2.5" />
            </svg>
            <div className="text-left">
              <div className="text-3xl font-extrabold tracking-tight leading-none">
                <span className="text-slate-900">sbc</span>
                <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">lite</span>
              </div>
              <div className="mt-1 text-[11px] tracking-[0.18em] uppercase text-slate-500">The SBC, simplified</div>
            </div>
          </div>

          <h2 className="mt-8 text-center text-xl font-semibold tracking-tight text-slate-900">
            {isSignUp ? 'Create your account' : 'Sign in'}
          </h2>
          <p className="mt-2 text-center text-sm text-slate-600">
            {isSignUp ? 'Set up the first admin, then invite the team.' : 'Use your email and password to continue.'}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4 border border-red-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {info && (
            <div className="rounded-md bg-blue-50 p-4 border border-blue-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">{info}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4 rounded-md shadow-sm">
            {isSignUp && (
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required={isSignUp}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 bg-white/80 px-3 py-2 placeholder-slate-400 shadow-sm focus:border-blue-600 focus:outline-none focus:ring-blue-600 sm:text-sm"
                  placeholder="Your name"
                />
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setError(null)
                  setInfo(null)
                }}
                className="mt-1 block w-full rounded-md border border-slate-300 bg-white/80 px-3 py-2 placeholder-slate-400 shadow-sm focus:border-blue-600 focus:outline-none focus:ring-blue-600 sm:text-sm"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 bg-white/80 px-3 py-2 placeholder-slate-400 shadow-sm focus:border-blue-600 focus:outline-none focus:ring-blue-600 sm:text-sm"
                placeholder="Password"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-slate-400"
            >
              {loading ? (
                <LoadingSpinner size="sm" className="text-white" />
              ) : isSignUp ? (
                'Sign up'
              ) : (
                'Sign in'
              )}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setIsSignUp(!isSignUp)
                setError(null)
                setInfo(null)
              }}
              className="text-sm text-blue-700 hover:text-cyan-600"
            >
              {isSignUp
                ? 'Already have an account? Sign in'
                : "Don't have an account? Sign up"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
